import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/authContext";

export interface Location {
  id: string;
  user_id: string;
  name: string;
  address: string | null;
  city: string | null;
  business_type: string | null;
  created_at: string;
  updated_at: string;
}

interface LocationContextType {
  locations: Location[];
  activeLocation: Location | null;
  setActiveLocationId: (id: string | null) => void;
  loading: boolean;
  refresh: () => Promise<void>;
}

const LocationContext = createContext<LocationContextType>({
  locations: [],
  activeLocation: null,
  setActiveLocationId: () => {},
  loading: true,
  refresh: async () => {},
});

export function LocationProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [locations, setLocations] = useState<Location[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchLocations = useCallback(async () => {
    if (!user) { setLocations([]); setLoading(false); return; }
    const { data } = await supabase
      .from("locations")
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: true });
    const locs = (data as Location[]) || [];
    setLocations(locs);

    // Restore saved active location or default to first
    const saved = localStorage.getItem(`oplif_active_location_${user.id}`);
    if (saved && locs.find(l => l.id === saved)) {
      setActiveId(saved);
    } else if (locs.length > 0) {
      setActiveId(locs[0].id);
    } else {
      setActiveId(null);
    }
    setLoading(false);
  }, [user]);

  useEffect(() => { fetchLocations(); }, [fetchLocations]);

  const setActiveLocationId = useCallback((id: string | null) => {
    setActiveId(id);
    if (user && id) localStorage.setItem(`oplif_active_location_${user.id}`, id);
  }, [user]);

  const activeLocation = locations.find(l => l.id === activeId) || null;

  return (
    <LocationContext.Provider value={{ locations, activeLocation, setActiveLocationId, loading, refresh: fetchLocations }}>
      {children}
    </LocationContext.Provider>
  );
}

export function useLocation() {
  return useContext(LocationContext);
}
